import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { reportApiFailure, reportApiSuccess } from '@/lib/apiHealth'
import { queryRetry, queryRetryDelay } from '@/lib/queryRetry'

/**
 * Single QueryClient per app tree. Defaults tuned for a dashboard:
 * - retry by kind of failure (``lib/queryRetry``): no answer from the backend
 *   (network, gateway page) up to 3 times with backoff; a 5xx the backend
 *   answered, 401 and unclassified errors once; 429 twice; other 4xx never.
 *   Mutations never retry (don't repeat a write)
 * - every query/mutation outcome feeds ``lib/apiHealth``, which drives the
 *   global "server unavailable" banner
 * - staleTime short enough to feel live, long enough to avoid refetch spam
 * - refetchOnWindowFocus stays on (default) for fresh data when tab returns
 */
export function QueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (err) => {
            reportApiFailure(err)
          },
          onSuccess: () => {
            reportApiSuccess()
          },
        }),
        mutationCache: new MutationCache({
          onError: (err) => {
            reportApiFailure(err)
          },
          onSuccess: () => {
            reportApiSuccess()
          },
        }),
        defaultOptions: {
          queries: {
            retry: queryRetry,
            retryDelay: queryRetryDelay,
            staleTime: 5_000,
          },
        },
      }),
  )

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>
}
